import React, { useEffect, useState } from "react";
import { Alert, Spin, Button } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FormAction } from "../../../types/enums";
import GETJwtToken from "../../../redux/authentication/authentication";
import { useLazyGetUserQuery } from "../../../redux/users/userAPI";
import { updateUserRefreshTable } from "../../../redux/users/userSlice";
import UsersCreateEditForm from "./UsersCreateEditForm";

export default function UsersEdit() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id = "" } = useParams<{ id: string }>();
  const normalizedUserId = decodeURIComponent(String(id ?? "")).trim();

  const [userData, setUserData] = useState<any>(null);
  const [loadError, setLoadError] = useState<string>("");

  const [triggerGetUser, { isLoading }] = useLazyGetUserQuery();

  useEffect(() => {
    async function loadUser() {
      if (!normalizedUserId || normalizedUserId === "[object Object]") {
        setLoadError("Invalid user id");
        return;
      }

      try {
        const token = await GETJwtToken();
        const response = await triggerGetUser({ id: normalizedUserId, token }).unwrap();
        const resolvedUser = response?.data ?? response;

        if (!resolvedUser) {
          setLoadError("User not found");
          return;
        }

        setUserData(resolvedUser);
      } catch {
        setLoadError("Failed to load user data");
      }
    }

    loadUser();
  }, [normalizedUserId, triggerGetUser]);

  const navigateToUsersAndRefresh = () => {
    dispatch(updateUserRefreshTable(true));
    setTimeout(() => {
      dispatch(updateUserRefreshTable(false));
    }, 1000);
    navigate("/organization-management/users");
  };

  if (isLoading) {
    return <Spin spinning tip="Loading" />;
  }

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Edit user"
        description={loadError}
        action={
          <Button type="link" onClick={() => navigate("/organization-management/users")}>
            Back to users
          </Button>
        }
      />
    );
  }

  if (!userData) {
    return null;
  }

  return (
    <UsersCreateEditForm
      mode={FormAction.Edit}
      userId={normalizedUserId}
      initialUser={userData}
      onSaveSuccess={navigateToUsersAndRefresh}
      onCancel={() => navigate("/organization-management/users")}
    />
  );
}
